import type { AnalyticsEvent } from '@atlassian/jira-common-analytics-v2-wrapped-components/src/types';
import type { AccountId, RefObject } from '@atlassian/jira-shared-types/src/general.tsx';
import type { AvatarDetails, AssigneeType } from '../../model/types';
import type { CroppingProperties } from '../../services/avatar/types';

export const PROJECT_NAME_CHANGE = 'state.ui.PROJECT_NAME_CHANGE' as const;
export const PROJECT_URL_CHANGE = 'state.ui.PROJECT_URL_CHANGE' as const;
export const PROJECT_KEY_CHANGE = 'state.ui.PROJECT_KEY_CHANGE' as const;
export const RELEASE_PROJECT_KEY = 'state.ui.PROJECT_KEY_RELEASE' as const;
export const UNDO_RELEASE_HISTORICAL_KEYS_CLICKED =
	'state.ui.UNDO_RELEASE_HISTORICAL_KEYS_CLICKED' as const;
export const PROJECT_DESCRIPTION_CHANGE = 'state.ui.PROJECT_DESCRIPTION_CHANGE' as const;
export const PROJECT_TEAM_TYPE_CHANGE = 'state.ui.PROJECT_TEAM_TYPE_CHANGE' as const;
export const PROJECT_TEAM_TYPE_FREE_TEXT_CHANGE =
	'state.ui.PROJECT_TEAM_TYPE_FREE_TEXT_CHANGE' as const;
export const DISMISS_FLAG = 'state.ui.DISMISS_FLAG' as const;

export const CLOSE_AVATAR_PICKER_DIALOG = 'state.ui.CLOSE_AVATAR_PICKER_DIALOG' as const;

export const ON_CHOOSE_PREDEFINED_AVATAR = 'state.ui.ON_CHOOSE_PREDEFINED_AVATAR' as const;

export const SHOW_FETCH_AVATARS_ERROR_FLAG = 'state.ui.SHOW_FETCH_AVATARS_ERROR_FLAG' as const;

export const UPLOAD_AVATAR = 'state.ui.UPLOAD_AVATAR' as const;
export const UPLOAD_AVATAR_SUCCESS = 'state.ui.UPLOAD_AVATAR_SUCCESS' as const;
export const UPLOAD_AVATAR_FAIL = 'state.ui.UPLOAD_AVATAR_FAIL' as const;

export const DELETE_PROJECT = 'state.ui.DELETE_PROJECT' as const;
export const DELETE_PROJECT_SUCCESS = 'state.ui.DELETE_PROJECT_SUCCESS' as const;
export const DELETE_PROJECT_FAIL = 'state.ui.DELETE_PROJECT_FAIL' as const;

export const OPEN_AVATAR_PICKER_DIALOG = 'state.ui.OPEN_AVATAR_PICKER_DIALOG' as const;

export const OPEN_DELETE_PROJECT_MODAL = 'state.ui.OPEN_DELETE_PROJECT_MODAL' as const;
export const CLOSE_DELETE_PROJECT_MODAL = 'state.ui.CLOSE_DELETE_PROJECT_MODAL' as const;

export const PRELOAD_AVATAR_IMAGE_SUCCESS = 'state.ui.PRELOAD_AVATAR_IMAGE_SUCCESS' as const;

export const SET_PROJECT_CATEGORY_ID = 'state.ui.SET_PROJECT_CATEGORY_ID' as const;

export const SET_PROJECT_LEAD_ACCOUNT_ID = 'state.ui.SET_PROJECT_LEAD_ACCOUNT_ID' as const;

export const TRASH_PROJECT_SUCCESS = 'state.ui.TRASH_PROJECT_SUCCESS' as const;

export const TRASH_PROJECT_FAIL = 'state.ui.TRASH_PROJECT_FAIL' as const;

export const OPEN_TRASH_PROJECT_MODAL = 'state.ui.OPEN_TRASH_PROJECT_MODAL' as const;

export const CLOSE_TRASH_PROJECT_MODAL = 'state.ui.CLOSE_TRASH_PROJECT_MODAL' as const;

export const ARCHIVE_PROJECT_SUCCESS = 'state.ui.ARCHIVE_PROJECT_SUCCESS' as const;

export const OPEN_ARCHIVE_PROJECT_MODAL = 'state.ui.OPEN_ARCHIVE_PROJECT_MODAL' as const;

export const CLOSE_ARCHIVE_PROJECT_MODAL = 'state.ui.CLOSE_ARCHIVE_PROJECT_MODAL' as const;

export const SAVE_BUTTON_CLICKED = 'state.ui.SAVE_BUTTON_CLICKED' as const;

export const FIELD_VALIDATION_FORCED = 'state.ui.FIELD_VALIDATION_FORCED' as const;

export const ASSIGNEE_TYPE_CHANGE = 'state.ui.ASSIGNEE_TYPE_CHANGE' as const;

export const SET_DROP_DOWN_MENU_BUTTON_REF = 'state.ui.SET_DROP_DOWN_MENU_BUTTON_REF' as const;

export type ChangeProjectNameAction = {
	type: typeof PROJECT_NAME_CHANGE;
	payload: string;
};

export type ReleaseProjectKeyAction = {
	type: typeof RELEASE_PROJECT_KEY;
	payload: {
		key: string;
		isReleased: boolean;
	};
};

export const projectNameChange = (value: string): ChangeProjectNameAction => ({
	type: PROJECT_NAME_CHANGE,
	payload: value,
});

export const onReleaseProjectKey = (key: string, isReleased: boolean): ReleaseProjectKeyAction => ({
	type: RELEASE_PROJECT_KEY,
	payload: { key, isReleased },
});

export type ChangeProjectKeyAction = {
	type: typeof PROJECT_KEY_CHANGE;
	payload: string;
};

export const undoReleaseHistoricalKeysClicked = () => ({
	type: UNDO_RELEASE_HISTORICAL_KEYS_CLICKED,
});

export const projectKeyChange = (value: string): ChangeProjectKeyAction => ({
	type: PROJECT_KEY_CHANGE,
	payload: value.toUpperCase(),
});

export type ChangeProjectUrlAction = {
	type: typeof PROJECT_URL_CHANGE;
	payload: string;
};

export const projectUrlChange = (value: string): ChangeProjectUrlAction => ({
	type: PROJECT_URL_CHANGE,
	payload: value,
});

export type ChangeProjectDescriptionAction = {
	type: typeof PROJECT_DESCRIPTION_CHANGE;
	payload: string;
};

export const projectDescriptionChange = (value: string): ChangeProjectDescriptionAction => ({
	type: PROJECT_DESCRIPTION_CHANGE,
	payload: value,
});

export type ChangeAssigneeTypeAction = {
	type: typeof ASSIGNEE_TYPE_CHANGE;
	payload: AssigneeType;
};

export const assigneeTypeChange = (assigneeType: AssigneeType): ChangeAssigneeTypeAction => ({
	type: ASSIGNEE_TYPE_CHANGE,
	payload: assigneeType,
});

export type DismissFlagTypeAction = {
	type: typeof DISMISS_FLAG;
	payload: {
		id: string | number;
	};
};

export const projectTeamTypeChange = (projectTeamType: string): ChangeProjectTeamTypeAction => ({
	type: PROJECT_TEAM_TYPE_CHANGE,
	payload: projectTeamType,
});

export type ChangeProjectTeamTypeAction = {
	type: typeof PROJECT_TEAM_TYPE_CHANGE;
	payload: string;
};
export const projectTeamTypeFreeTextChange = (
	projectTeamTypeFreeText: string | undefined,
	error: boolean,
): ChangeProjectTeamTypeFreeTextAction => ({
	type: PROJECT_TEAM_TYPE_FREE_TEXT_CHANGE,
	payload: projectTeamTypeFreeText,
	error,
});

export type ChangeProjectTeamTypeFreeTextAction = {
	type: typeof PROJECT_TEAM_TYPE_FREE_TEXT_CHANGE;
	payload: string | undefined;
	error: boolean;
};

export const dismissFlag = (id: string | number): DismissFlagTypeAction => ({
	type: DISMISS_FLAG,
	payload: { id },
});

export type ShowFetchAvatarsErrorFlagAction = {
	type: typeof SHOW_FETCH_AVATARS_ERROR_FLAG;
};

export const showFetchAvatarsErrorFlag = (): ShowFetchAvatarsErrorFlagAction => ({
	type: SHOW_FETCH_AVATARS_ERROR_FLAG,
});

export type UploadAvatarAction = {
	type: typeof UPLOAD_AVATAR;
	payload: {
		file: File;
		croppingProperties: CroppingProperties;
	};
	meta: {
		analyticsEvent: AnalyticsEvent;
	};
};

export const uploadAvatar = (
	file: File,
	croppingProperties: CroppingProperties,
	analyticsEvent: AnalyticsEvent,
): UploadAvatarAction => ({
	type: UPLOAD_AVATAR,
	payload: { file, croppingProperties },
	meta: { analyticsEvent },
});

export type UploadAvatarSuccessAction = {
	type: typeof UPLOAD_AVATAR_SUCCESS;
	payload: AvatarDetails;
	meta: {
		analyticsEvent: AnalyticsEvent;
	};
};

export const uploadAvatarSuccess = (
	data: AvatarDetails,
	analyticsEvent: AnalyticsEvent,
): UploadAvatarSuccessAction => ({
	type: UPLOAD_AVATAR_SUCCESS,
	payload: data,
	meta: { analyticsEvent },
});

export type OpenAvatarPickerDialogAction = {
	type: typeof OPEN_AVATAR_PICKER_DIALOG;
};

export const openAvatarPickerDialog = () => ({
	type: OPEN_AVATAR_PICKER_DIALOG,
});

export type UploadAvatarFailAction = {
	type: typeof UPLOAD_AVATAR_FAIL;
	payload: {
		error: Error;
	};
};

export const uploadAvatarFail = (error: Error): UploadAvatarFailAction => ({
	type: UPLOAD_AVATAR_FAIL,
	payload: { error },
});

export type CloseAvatarPickerDialogAction = {
	type: typeof CLOSE_AVATAR_PICKER_DIALOG;
};

export type UndoReleaseHistoricalKeysClickedAction = {
	type: typeof UNDO_RELEASE_HISTORICAL_KEYS_CLICKED;
};

export const closeAvatarPickerDialog = () => ({
	type: CLOSE_AVATAR_PICKER_DIALOG,
});

export type OnChoosePredefinedAvatarAction = {
	type: typeof ON_CHOOSE_PREDEFINED_AVATAR;
	payload: AvatarDetails;
};

export const onChoosePredefinedAvatar = (avatar: AvatarDetails) => ({
	type: ON_CHOOSE_PREDEFINED_AVATAR,
	payload: avatar,
});

export type DeleteProjectAction = {
	type: typeof DELETE_PROJECT;
	meta: {
		analyticsEvent: AnalyticsEvent;
	};
};

export const deleteProject = (analyticsEvent: AnalyticsEvent): DeleteProjectAction => ({
	type: DELETE_PROJECT,
	meta: { analyticsEvent },
});

export type DeleteProjectSuccessAction = {
	type: typeof DELETE_PROJECT_SUCCESS;
};

export const deleteProjectSuccess = (): DeleteProjectSuccessAction => ({
	type: DELETE_PROJECT_SUCCESS,
});

export type DeleteProjectFailAction = {
	type: typeof DELETE_PROJECT_FAIL;
	payload: {
		error: Error;
	};
};

export const deleteProjectFail = (error: Error): DeleteProjectFailAction => ({
	type: DELETE_PROJECT_FAIL,
	payload: { error },
});

export type OpenDeleteProjectModalAction = {
	type: typeof OPEN_DELETE_PROJECT_MODAL;
};

export const openDeleteProjectModal = (): OpenDeleteProjectModalAction => ({
	type: OPEN_DELETE_PROJECT_MODAL,
});

export type CloseDeleteProjectModalAction = {
	type: typeof CLOSE_DELETE_PROJECT_MODAL;
};

export const closeDeleteProjectModal = (): CloseDeleteProjectModalAction => ({
	type: CLOSE_DELETE_PROJECT_MODAL,
});

export type PreloadAvatarImageSuccessAction = {
	type: typeof PRELOAD_AVATAR_IMAGE_SUCCESS;
};

export const preloadAvatarImageSuccess = (): PreloadAvatarImageSuccessAction => ({
	type: PRELOAD_AVATAR_IMAGE_SUCCESS,
});

export type SetProjectCategoryIdAction = {
	type: typeof SET_PROJECT_CATEGORY_ID;
	payload: string;
};

export const setProjectCategoryId = (projectCategoryId: string): SetProjectCategoryIdAction => ({
	type: SET_PROJECT_CATEGORY_ID,
	payload: projectCategoryId,
});

export type SetProjectLeadAccountIdAction = {
	type: typeof SET_PROJECT_LEAD_ACCOUNT_ID;
	payload: AccountId;
};
export const setProjectLeadAccountId = (
	projectLeadAccountId: AccountId,
): SetProjectLeadAccountIdAction => ({
	type: SET_PROJECT_LEAD_ACCOUNT_ID,
	payload: projectLeadAccountId,
});

export type TrashProjectSuccessAction = {
	type: typeof TRASH_PROJECT_SUCCESS;
	payload: {
		replace: (href: string) => void;
	};
};

export const trashProjectSuccess = (
	replace: (href: string) => void,
): TrashProjectSuccessAction => ({
	type: TRASH_PROJECT_SUCCESS,
	payload: { replace },
});

export type TrashProjectFailAction = {
	type: typeof TRASH_PROJECT_FAIL;
};

export const trashProjectFail = (): TrashProjectFailAction => ({
	type: TRASH_PROJECT_FAIL,
});

export type OpenTrashProjectModalAction = {
	type: typeof OPEN_TRASH_PROJECT_MODAL;
};

export const openTrashProjectModal = (): OpenTrashProjectModalAction => ({
	type: OPEN_TRASH_PROJECT_MODAL,
});

export type CloseTrashProjectModalAction = {
	type: typeof CLOSE_TRASH_PROJECT_MODAL;
};

export const closeTrashProjectModal = (): CloseTrashProjectModalAction => ({
	type: CLOSE_TRASH_PROJECT_MODAL,
});

export type ArchiveProjectSuccessAction = {
	type: typeof ARCHIVE_PROJECT_SUCCESS;
	payload: {
		replace: (path: string) => void;
	};
};

export const archiveProjectSuccess = (
	replace: (path: string) => void,
): ArchiveProjectSuccessAction => ({
	type: ARCHIVE_PROJECT_SUCCESS,
	payload: { replace },
});

export type OpenArchiveProjectModalAction = {
	type: typeof OPEN_ARCHIVE_PROJECT_MODAL;
};

export const openArchiveProjectModal = (): OpenArchiveProjectModalAction => ({
	type: OPEN_ARCHIVE_PROJECT_MODAL,
});

export type CloseArchiveProjectModalAction = {
	type: typeof CLOSE_ARCHIVE_PROJECT_MODAL;
};

export const closeArchiveProjectModal = (): CloseArchiveProjectModalAction => ({
	type: CLOSE_ARCHIVE_PROJECT_MODAL,
});

export type SaveButtonClickedAction = {
	type: typeof SAVE_BUTTON_CLICKED;
	meta: {
		analyticsEvent: AnalyticsEvent;
	};
};

export const saveButtonClicked = (analyticsEvent: AnalyticsEvent): SaveButtonClickedAction => ({
	type: SAVE_BUTTON_CLICKED,
	meta: {
		analyticsEvent,
	},
});

export type FieldValidationForcedAction = {
	type: typeof FIELD_VALIDATION_FORCED;
	payload: string | undefined;
};

export const fieldValidationForced = (name?: string): FieldValidationForcedAction => ({
	type: FIELD_VALIDATION_FORCED,
	payload: name,
});

export type SetDropDownMenuButtonRefAction = {
	type: typeof SET_DROP_DOWN_MENU_BUTTON_REF;
	payload: RefObject;
};

export const setDropDownMenuButtonRef = (
	dropDownMenuButtonRef: RefObject,
): SetDropDownMenuButtonRefAction => ({
	type: SET_DROP_DOWN_MENU_BUTTON_REF,
	payload: dropDownMenuButtonRef,
});

export type ProjectDetailsUIAction =
	| ChangeProjectNameAction
	| ChangeProjectKeyAction
	| ChangeProjectUrlAction
	| ReleaseProjectKeyAction
	| UndoReleaseHistoricalKeysClickedAction
	| ChangeProjectDescriptionAction
	| ChangeAssigneeTypeAction
	| ChangeProjectTeamTypeAction
	| ChangeProjectTeamTypeFreeTextAction
	| DismissFlagTypeAction
	| UploadAvatarAction
	| UploadAvatarSuccessAction
	| UploadAvatarFailAction
	| DeleteProjectAction
	| DeleteProjectSuccessAction
	| DeleteProjectFailAction
	| OpenAvatarPickerDialogAction
	| OpenDeleteProjectModalAction
	| CloseDeleteProjectModalAction
	| CloseAvatarPickerDialogAction
	| OnChoosePredefinedAvatarAction
	| PreloadAvatarImageSuccessAction
	| SetProjectCategoryIdAction
	| SetProjectLeadAccountIdAction
	| ShowFetchAvatarsErrorFlagAction
	| TrashProjectSuccessAction
	| TrashProjectFailAction
	| OpenTrashProjectModalAction
	| CloseTrashProjectModalAction
	| ArchiveProjectSuccessAction
	| OpenArchiveProjectModalAction
	| CloseArchiveProjectModalAction
	| SaveButtonClickedAction
	| FieldValidationForcedAction
	| SetDropDownMenuButtonRefAction;
